<template>
    <f-fullscreen-modal ref="modal">
        <f-app-screen>
            <f-modal-header @close="close()">{{ $t('settings.title') }}</f-modal-header>

            <f-app-body>
                <template v-if="isAuth">
                    <div class="py-10 text-center">
                        {{ $t('settings.nickname') }}
                        <div class="text-lg font-bold">{{ user.nickname }}</div>

                        <f-button class="text-sky" icon="edit" @click="showProfile()">
                            {{ $t('edit.profile') }}
                        </f-button>
                    </div>

                    <f-divider/>
                </template>

                <div class="py-10 text-center">
                    <a
                            class="text-sky underline tracking-wider"
                            href="https://www.instagram.com/forexheroapp/"
                            target="_blank"
                    >
                        <img
                                :alt="$t('settings.follow.instagram')"
                                class="inline-block w-16 h-16 ltr:mr-4 rtl:ml-4 object-contain"
                                src="@/assets/icons/follow-ig@2x.png"
                                srcset="@/assets/icons/follow-ig@2x.png 2x, @/assets/icons/follow-ig@3x.png 3x, @/assets/icons/follow-ig@4x.png 4x"
                        />
                        <span>{{ $t('settings.follow.instagram') }}</span>
                    </a>
                </div>

                <f-divider/>

                <div class="py-8">
                    <div class="flex justify-between align-middle px-4 py-2">
                        <label class="tracking-wider" for="soundToggle">{{ $t('settings.sound') }}</label>
                        <toggle id="soundToggle" :model-value="sound" @update:model-value="toggleSound()"/>
                    </div>

                    <div class="flex justify-between align-middle px-4 py-2">
                        <label class="tracking-wider" for="vibrationToggle">{{ $t('settings.vibration') }}</label>
                        <toggle id="vibrationToggle" :model-value="vibration" @update:model-value="toggleVibration()"/>
                    </div>

                    <div class="flex justify-between align-middle px-4 py-2">
                        <label class="tracking-wider" for="vibrationToggle">{{ $t('settings.notifications') }}</label>
                        <toggle
                                id="notificationsToggle"
                                :model-value="notifications"
                                @update:model-value="toggleNotifications"
                        />
                    </div>
                </div>

                <f-divider/>

                <div v-if="isAuth" class="py-10 px-4 text-center">
                    <p class="mb-8 text-sm">{{ $t('settings.sign.out.title') }}</p>

                    <f-button
                            :loading="authenticating"
                            :disable="authenticating"
                            @click="safeSignOut()"
                            class="mx-auto rounded-lg bg-backdrop-800"
                    >
                        {{ $t('settings.sign.out.button') }}
                    </f-button>
                </div>

                <div v-else class="py-10 px-4 text-center flex flex-col">
                    <p class="mb-8 text-sm">{{ $t('settings.facebook.should.connect') }}</p>

                    <div class="inline-flex flex-col mx-auto">
                        <facebook-button>
                            {{ $t('settings.sign.in.facebook.button') }}
                        </facebook-button>

                        <apple-button v-if="isIOS" class="mt-6">
                            {{ $t('settings.sign.in.apple.button') }}
                        </apple-button>
                    </div>
                </div>

                <f-divider/>

                <div class="py-10 flex flex-col flex-nowrap">
                    <p class="mb-4 text-center">{{ $t('choose.language') }}</p>

                    <div class="inline-grid grid-cols-2 xs3:grid-cols-3 px-4 sm:px-4 md:px-6 gap-2 md:gap-6">
                        <lang-item
                                v-for="(lang) in languages"
                                :key="lang.code"
                                :code="lang.code"
                                :name="lang.name"
                                @click="setUserLanguage"
                                :class="{ 'bg-slate-blue-900': lang.code === currentLanguage, 'rounded': true }"
                                :flag-classes="['w-6']"
                        />
                    </div>
                </div>

                <f-divider/>

                <div v-if="isEntitled && !isLifetime" class="py-10 text-center">
                    <a :href="managementUrl" target="_blank" class="underline tracking-wide cursor-pointer">
                        {{ $t('settings.subscription.cancel') }}
                    </a>
                </div>

                <div v-if="!isEntitled" class="py-10 text-center">
                    <link-button
                            class="text-sky"
                            @click="restoreSubscription()"
                    >
                        {{ $t('settings.subscription.redeem') }}
                    </link-button>
                </div>

                <f-divider/>

                <div class="py-10 text-center">
                    <link-button
                            class="text-sky"
                            @click="$refs.privacyPolicyModal.open()"
                    >
                        {{ $t('privacy.policy') }}
                    </link-button>

                    <div class="mt-2 text-xs text-backdrop-200">{{ version }}</div>
                </div>

                <f-divider/>

                <div class="py-10 text-center">
                    <link-button
                            v-if="!idVisible"
                            class="text-sky"
                            @click="idVisible = true"
                    >
                        {{ $t('settings.show.id') }}
                    </link-button>

                    <template v-else>
                        <p class="ext-sm">{{ $t('settings.user.id.title') }}</p>
                        <div class="mt-4 text-sm">{{ userId }}</div>
                    </template>
                </div>
            </f-app-body>
        </f-app-screen>

        <privacy-policy-modal ref="privacyPolicyModal"/>
        <profile-modal ref="profileModal"/>
        <confirm-sign-out-dialog ref="confirmSignOutDialog"/>
        <enable-notifications ref="enableNotifications" :z="90"/>
    </f-fullscreen-modal>
</template>

<script>
import LocalesService from '@/services/LocalesService'
import HapticsService from '@/services/HapticsService'
import FacebookButton from '@/components/FacebookButton.vue'
import LangItem from '@/components/LangItem.vue'
import Toggle from '@/components/Toggle.vue'
import ProfileModal from '@/modules/settings/ProfileModal.vue'
import PrivacyPolicyModal from '@/modules/settings/PrivacyPolicyModal.vue'
import LinkButton from '@/components/LinkButton.vue'
import Settings from '@/store/Settings'
import User from '@/store/User'
import Facebook from '@/services/Facebook'
import Analytics, { SCREEN_SETTINGS } from '/src/services/Analytics'
import ConfirmSignOutDialog from '/src/modules/settings/ConfirmSignOutModal.vue'
import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import SubService, { ACCESS_PRO, ENTITLEMENT_PRO } from '/src/services/SubService'
import AppleButton from '/src/components/AppleButton.vue'
import AuthService from '@/services/AuthService'
import AuthUserStateService from '@/services/AuthUserStateService'
import AppService from '@/services/AppService'
import SoundService from '@/services/SoundService'
import moneySound from '@/assets/sounds/money.mp3'
import NotificationsService from '@/services/NotificationsService.js'
import EnableNotifications from '@/modules/core/EnableNotifications.vue'
import Crashlytics from '@/services/Crashlytics.js'

export default {
  name: 'SettingsModal',

  components: {
    EnableNotifications,
    ConfirmSignOutDialog,
    LinkButton,
    PrivacyPolicyModal,
    ProfileModal,
    Toggle,
    LangItem,
    FacebookButton,
    AppleButton
  },

  data () {
    return {
      authenticating: false,
      version: null,
      contentVersion: null,
      idVisible: false,
      appInstanceId: null,
      managementUrl: null
    }
  },

  computed: {
    isEntitled: function () {
      return SubService.isPro()
    },
    isLifetime: function () {
      return this.isEntitled && SubService.isLifetime()
    },
    languages: function () {
      return LocalesService.languages.available
    },
    sound: function () {
      return Settings.getSound()
    },
    vibration: function () {
      return Settings.getVibration()
    },
    notifications: function () {
      return Settings.getNotifications()
    },
    language: function () {
      return this.$i18n.locale
    },
    user: function () {
      return User.getUser()
    },
    isAuth: function () {
      return AuthService.isAuth()
    },
    isFacebookAuth: function () {
      return Facebook.store().isAuth
    },
    userId: function () {
      return this.appInstanceId
    },
    isIOS: function () {
      return Capacitor.getPlatform() === 'ios'
    },
    currentLanguage: function () {
      return this.$i18n.locale
    }
  },

  methods: {
    async open () {
      await SubService.refreshCustomerInfo()
      await this.$refs.modal.open()
      await Analytics.setScreenName(SCREEN_SETTINGS)


      // Request notifications if setting not initialized yet
      if (this.notifications === null) {
        await Settings.enableNotifications(true)
          .catch(e => Crashlytics.recordExceptionWithStacktrace('Notifications initialization failed', e))
      }
    },
    close () {
      this.idVisible = false
      this.$refs.modal.close()
    },
    showProfile () {
      this.$refs.profileModal.open()
    },
    async setUserLanguage (code) {
      HapticsService.impact()
      await LocalesService.setAppLanguage(code)
      await Settings.setLanguage(code)
    },
    toggleSound () {
      HapticsService.impact()
      Settings.toggleSound()
      if (this.sound) {
        SoundService.play('money', moneySound)
      }
    },
    toggleVibration () {
      HapticsService.impact()
      Settings.toggleVibration()
    },
    async toggleNotifications (val) {
      if (val) {
        // enabling
        const perms = await NotificationsService.checkPermissions()

        if (perms === 'denied') {
          await this.$refs.enableNotifications.open()
          const perms = await NotificationsService.checkPermissions()
          if (perms === 'granted') {
            await Settings.enableNotifications(true)
          }
        } else {
          await Settings.enableNotifications(true)
        }
      } else {
        // disabling
        await Settings.enableNotifications(false)
      }

      HapticsService.impact()
    },
    async restoreSubscription () {
      this.restoring = this.$toasted.show(
        this.$t('settings.subscription.redeeming.notification'),
        {
          duration: 0
        }
      )

      try {
        const customerInfo = await SubService.restore()

        const message = customerInfo?.entitlements && Object.keys(customerInfo.entitlements.active).includes(ENTITLEMENT_PRO)
          ? this.$t('settings.subscription.redeeming.success')
          : this.$t('settings.subscription.redeeming.not.found')

        this.restoring
          .text(message)
          .goAway(3000)
      } catch (e) {
        this.restoring
          .text(this.$t('settings.subscription.redeeming.error'))
          .goAway(3000)
      }
    },
    safeSignOut () {
      if (AuthUserStateService.isDirty()) {
        this.$refs.confirmSignOutDialog.open()
          .onOk(() => this.signOutAll())
      } else {
        this.signOutAll()
      }
    },
    async signOutAll () {
      try {
        await Facebook.logout()
        await AuthService.logout()
      } catch (error) {
        console.error('Error signing out:', error)
      }
    },
  },

  async created () {
    if (Capacitor.isNativePlatform()) {
      try {
        const {
          version,
          build
        } = await App.getInfo()
        this.version = `${version} (${build})`
        this.appInstanceId = AppService.getAppId()
      } catch (e) {
        console.warn('Failed getting app version and instance id')
      }

      try {
        this.managementUrl = await SubService.getManagementUrl()
      } catch (e) {
        console.warn('Failed getting management url')
      }
    }
  }
}
</script>
